import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCatalogService, PipelineCredentialResponse } from '../services/catalogService';
import { extractErrorMessage } from '../../../shared/utils/errorUtils';

export const pipelineCredentialKeys = {
  list: (serviceId: string | null) => ['service-administration', 'pipeline-credentials', serviceId] as const,
};

export function usePipelineCredentials(serviceId: string | null) {
  const catalogService = useCatalogService();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: pipelineCredentialKeys.list(serviceId),
    queryFn: async () => {
      const result = await catalogService.listPipelineCredentials(serviceId as string);
      return result.credentials;
    },
    enabled: !!serviceId,
  });

  // Derive the load error from the query; a dismissed flag (reset when a new
  // error arrives) lets the consumer dismiss it — no syncing error into state.
  const [errorDismissed, setErrorDismissed] = useState(false);
  useEffect(() => {
    if (query.error) setErrorDismissed(false);
  }, [query.error]);
  const error = query.error && !errorDismissed
    ? extractErrorMessage(query.error, 'Failed to load pipeline credentials')
    : null;
  const dismissError = useCallback(() => setErrorDismissed(true), []);

  const createMutation = useMutation({
    mutationFn: (label: string) => {
      if (!serviceId) throw new Error('No service selected');
      return catalogService.createPipelineCredential({ service_id: serviceId, label });
    },
    onSuccess: (credential) => {
      queryClient.setQueryData<PipelineCredentialResponse[]>(
        pipelineCredentialKeys.list(serviceId),
        (prev) => [credential, ...(prev ?? [])],
      );
    },
  });
  const { mutateAsync: createAsync } = createMutation;
  const createCredential = useCallback(
    (label: string): Promise<PipelineCredentialResponse> => createAsync(label),
    [createAsync],
  );

  const revokeMutation = useMutation({
    mutationFn: (clientId: string) => {
      if (!serviceId) throw new Error('No service selected');
      return catalogService.revokePipelineCredential(serviceId, clientId);
    },
    onSuccess: (_result, clientId) => {
      queryClient.setQueryData<PipelineCredentialResponse[]>(
        pipelineCredentialKeys.list(serviceId),
        (prev) =>
          (prev ?? []).map((c) =>
            c.client_id === clientId
              ? { ...c, status: 'revoked' as const, revoked_at: new Date().toISOString() }
              : c,
          ),
      );
    },
  });
  const { mutateAsync: revokeAsync } = revokeMutation;
  const revokeCredential = useCallback(
    async (clientId: string) => {
      await revokeAsync(clientId);
    },
    [revokeAsync],
  );

  const { refetch } = query;
  const reload = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    credentials: (query.data ?? []) as PipelineCredentialResponse[],
    loading: query.isFetching,
    error,
    dismissError,
    createCredential,
    revokeCredential,
    reload,
  };
}
